import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Send, Delete, Refresh, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchNotifications,
  createNotification,
  updateNotification,
  sendNotification,
  fetchTenants,
} from '../../../store/slices/superAdminSlice';
import { CreateNotification, NotificationDto } from '../../../types/superAdminTypes';
import { superAdminApi } from '../../../api/superAdminApi';

const NotificationsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { notifications, tenants, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const ALL_TENANTS_VALUE = '__ALL_TENANTS__';
  const [errors, setErrors] = useState({
  title: '',
  message: '',
  channel: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingNotificationId, setEditingNotificationId] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [dialogApiError, setDialogApiError] = useState<string | null>(null);
  const [sendingNotificationIds, setSendingNotificationIds] = useState<string[]>([]);
  const [form, setForm] = useState<CreateNotification>({ title: '', message: '', channel: 0, targetTenantIds: [] });

  const fetchData = useCallback(() => { dispatch(fetchNotifications({})); }, [dispatch]);

  useEffect(() => {
    fetchData();
    dispatch(fetchTenants({}));
  }, [fetchData, dispatch]);


  const validate = () => {
  const newErrors: any = {};

  if (!form.title.trim()) newErrors.title = 'Title is required';
  if (!form.message.trim()) newErrors.message = 'Message is required';
  if (form.channel === null || form.channel === undefined)
    newErrors.channel = 'Channel is required';

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
  };

  const resetDialogState = () => {
    setErrors({ title: '', message: '', channel: '' });
    setDialogApiError(null);
    setSubmitLoading(false);
    setEditingNotificationId(null);
    setDialogMode('create');
    setForm({ title: '', message: '', channel: 0, targetTenantIds: [] });
  };

  const handleOpenCreate = () => {
    resetDialogState();
    setOpenDialog(true);
  };

  const handleOpenEdit = (notif: NotificationDto) => {
    setErrors({ title: '', message: '', channel: '' });
    setDialogApiError(null);
    setSubmitLoading(false);
    setDialogMode('edit');
    setEditingNotificationId(notif.id);

    const tenantIds = Array.isArray(notif.targetTenantIds) ? notif.targetTenantIds : [];
    const normalizedTargetTenantIds =
      tenants.length > 0 && tenantIds.length === tenants.length ? [] : Array.from(new Set(tenantIds));

    setForm({
      title: notif.title ?? '',
      message: notif.message ?? '',
      channel: typeof notif.channel === 'number' ? notif.channel : 0,
      targetTenantIds: normalizedTargetTenantIds,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (submitLoading) return;
    setOpenDialog(false);
    resetDialogState();
  };

  const handleTargetTenantsChange = (value: string[]) => {
    if (value.includes(ALL_TENANTS_VALUE)) {
      setForm({ ...form, targetTenantIds: [] });
      return;
    }
    setForm({ ...form, targetTenantIds: Array.from(new Set(value)) });
  };

  const handleSubmitDialog = async () => {
    if (submitLoading) return;
    try {
      if (!validate()) return;
      setSubmitLoading(true);
      setDialogApiError(null);
      let notificationForm = { ...form };
      // Empty targetTenantIds = All Tenants (handled by backend)
      if (dialogMode === 'edit' && editingNotificationId) {
        await dispatch(updateNotification({ id: editingNotificationId, data: notificationForm })).unwrap();
        toast.success('Notification updated');
      } else {
        await dispatch(createNotification(notificationForm)).unwrap();
        toast.success('Notification created');
      }
      setOpenDialog(false);
      resetDialogState();
      fetchData();
    } catch (err: any) {
      const message =
        (typeof err === 'string' && err) ||
        (err?.message as string) ||
        (dialogMode === 'edit' ? 'Failed to update notification' : 'Failed to create notification');
      setDialogApiError(message);
      toast.error(message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSend = async (id: string) => {
    if (sendingNotificationIds.includes(id)) return;
    try {
      setSendingNotificationIds((prev) => [...prev, id]);
      await dispatch(sendNotification(id)).unwrap();
      toast.success('Notification sent');
      fetchData();
    } catch (err: any) {
      const message = (typeof err === 'string' && err) || (err?.message as string) || 'Failed to send notification';
      toast.error(message);
    } finally {
      setSendingNotificationIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({ title: 'Delete Notification?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f44336', confirmButtonText: 'Delete' });
    if (result.isConfirmed) {
      try {
        await superAdminApi.deleteNotification(id);
        toast.success('Deleted');
        fetchData();
      } catch (err: any) {
        toast.error('Failed to delete');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">Send announcements to tenants</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>Create</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Channel</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Sent</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : notifications.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No notifications found</Typography></TableCell></TableRow>
            ) : (
              notifications.map((notif) => (
                <TableRow key={notif.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{notif.title}</Typography></TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{notif.message}</Typography></TableCell>
                  <TableCell><Chip label={notif.channel === 0 ? 'Email' : 'In-App'} size="small" color={notif.channel === 0 ? 'info' : 'secondary'} /></TableCell>
                  <TableCell>
                    <Chip label={notif.isSent ? 'Sent' : 'Draft'} color={notif.isSent ? 'success' : 'default'} size="small" />
                    {notif.sentAt && <Typography variant="caption" display="block" color="text.secondary">{new Date(notif.sentAt).toLocaleDateString()}</Typography>}
                  </TableCell>
                  <TableCell>{new Date(notif.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">
                    {!notif.isSent && (
                      <>
                        <Tooltip title={sendingNotificationIds.includes(notif.id) ? 'Sending...' : 'Send'}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={sendingNotificationIds.includes(notif.id)}
                              onClick={() => handleSend(notif.id)}
                            >
                              {sendingNotificationIds.includes(notif.id) ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="info"
                            disabled={sendingNotificationIds.includes(notif.id)}
                            onClick={() => handleOpenEdit(notif)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(notif.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{dialogMode === 'edit' ? 'Edit Notification' : 'Create Notification'}</DialogTitle>
        <DialogContent>
          {dialogApiError && <Alert severity="error" sx={{ mt: 1 }}>{dialogApiError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
              fullWidth
              label="Title*"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              error={!!errors.title}
              helperText={errors.title}
              placeholder='Title'
            />
            </Grid>
            <Grid size={{ xs: 12 }}>
             <TextField
              fullWidth
              label="Message*"
              multiline
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              error={!!errors.message}
              helperText={errors.message}
              placeholder='Message'
             />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.channel}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={form.channel}
                label="Channel"
                onChange={(e) => setForm({ ...form, channel: e.target.value as number })}
              >
                <MenuItem value={0}>Email</MenuItem>
                <MenuItem value={1}>In-App</MenuItem>
              </Select>
              <Typography variant="caption" color="error">
                {errors.channel}
              </Typography>
            </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Target Tenants</InputLabel>
                <Select
                  multiple
                  value={form.targetTenantIds}
                  label="Target Tenants"
                  onChange={(e) => handleTargetTenantsChange(e.target.value as string[])}
                  renderValue={(selected) => {
                    const selectedIds = selected as string[];
                    if (selectedIds.length === 0) return 'All Tenants';
                    if (tenants.length === 0) return `${selectedIds.length} tenant(s) selected`;
                    return selectedIds
                      .map((id) => tenants.find((t) => t.id === id)?.companyName || id)
                      .join(', ');
                  }}
                  MenuProps={{
                    PaperProps: { style: { maxHeight: 360 } },
                  }}
                >
                  <MenuItem value={ALL_TENANTS_VALUE}>
                    <Checkbox checked={form.targetTenantIds.length === 0} />
                    <ListItemText primary="All Tenants" />
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={form.targetTenantIds.includes(t.id)} />
                      <ListItemText primary={t.companyName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">Leave empty to send to all tenants</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={submitLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitDialog}
            disabled={submitLoading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {submitLoading ? (dialogMode === 'edit' ? 'Updating...' : 'Creating...') : (dialogMode === 'edit' ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsPage;
